import { onDocumentReady, prefersReducedMotion } from '@theme/utilities';

/** @type {IntersectionObserver | null} */
let observer = null;

const VIEWPORT_THRESHOLD = 0.95;
const FIRST_SECTION_BASE_DELAY_MS = 220;
const IN_VIEW_BASE_DELAY_MS = 80;
const REVIEWS_FLIP_STAGGER_MS = 200;
const HOW_IT_WORKS_STAGGER_MS = 180;
const STEP_REVEAL_DURATION_MS = 950;
const PRODUCT_CARD_FLIP_STAGGER_DESKTOP_MS = 90;
const PRODUCT_CARD_FLIP_DURATION_MS = 1150;
const PRODUCT_CARD_FLIP_DESKTOP_MQ = '(min-width: 750px)';
const MOBILE_REVEAL_MQ = '(max-width: 749px)';
const AUTHORITY_TICKER_PILL_STAGGER_MS = 110;
/**
 * @returns {boolean}
 */
function isMobileRevealViewport() {
  return window.matchMedia(MOBILE_REVEAL_MQ).matches;
}

/**
 * @returns {number}
 */
function getReviewsRevealStaggerMs() {
  return isMobileRevealViewport() ? HOW_IT_WORKS_STAGGER_MS : REVIEWS_FLIP_STAGGER_MS;
}

/**
 * @returns {number}
 */
function getReviewsRevealDurationMs() {
  return isMobileRevealViewport() ? STEP_REVEAL_DURATION_MS : 850;
}

/** @type {IntersectionObserver | null} */
let authorityTickerObserver = null;

/** @type {IntersectionObserver | null} */
let reviewsGridObserver = null;

/** @type {IntersectionObserver | null} */
let howItWorksGridObserver = null;

/** @type {IntersectionObserver | null} */
let solutionCardsObserver = null;

/** @type {IntersectionObserver | null} */
let productCardsListObserver = null;

const PRODUCT_CARD_SELECTOR = '.topeak-product-grid__card, .topeak-shop-all-product-grid__card';
const PRODUCT_GRID_SECTION_SELECTOR = '.topeak-product-grid, .topeak-shop-all-product-grid';
const PRODUCT_CARDS_REVEAL_EVENT = 'topeak:reveal-product-cards';
const ROTATE_OUT_IMAGE_SELECTOR = '.topeak-about-hero-2__product-image, .topeak-contact-faq__media-image';
const ROTATE_OUT_MEDIA_SELECTOR = '.topeak-about-hero-2__media, .topeak-contact-faq__media';

const LAYOUT_CONTAINER_PATTERN =
  '[class*="__grid"], [class*="__layout"], [class*="__comparison"], [class*="__cards"], [class*="__features"], [class*="__steps"]';

const GRID_SELECTORS = [
  '[class*="__grid"]',
  '[class*="__comparison"]',
  '[class*="__cards"]',
  '[class*="__layout"]',
  '[class*="__features"]',
  '[class*="__steps"]',
].join(', ');

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight * VIEWPORT_THRESHOLD && rect.bottom > 0;
}

/**
 * @param {HTMLElement} element
 * @param {boolean} isFirstSection
 * @returns {boolean}
 */
function isValidTarget(element, isFirstSection) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.classList.contains('topeak-reveal')) return false;
  if (element.closest('[data-topeak-reveal="off"]')) return false;
  if (isComparisonColumn(element) && isMobileRevealViewport()) return false;
  if (element.getAttribute('aria-hidden') === 'true' && !isOriginStory1ImageWrap(element)) return false;
  if (element.matches('[class*="__bg"], [class*="__background"], [class*="__gradient"]')) return false;

  if (isFirstSection && element.matches('h1, [class*="__heading"]')) {
    return false;
  }

  return true;
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isLayoutContainer(element) {
  return element.matches(LAYOUT_CONTAINER_PATTERN);
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isCardElement(element) {
  return [...element.classList].some((className) => /__card(?:--|$)/.test(className));
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isReviewsGalleryTile(element) {
  return element.matches('.topeak-reviews-1__tile');
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isHowItWorksStep(element) {
  return element.matches('.topeak-how-it-works__step');
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isSolutionCard(element) {
  return element.matches('.topeak-solution__card');
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isProductCard(element) {
  return element.matches(PRODUCT_CARD_SELECTOR);
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isOriginStory1ImageWrap(element) {
  return element.matches('.topeak-about-origin-story-1__item-image-wrap');
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isRotateOutImage(element) {
  return element.matches(ROTATE_OUT_IMAGE_SELECTOR);
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isComparisonColumn(element) {
  return element.matches('.topeak-comparison__col');
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isAuthorityTickerPill(element) {
  return element.matches('.topeak-authority-ticker__pill');
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function shouldFlip(element) {
  if (element.dataset.topeakReveal === 'flip') return true;
  if (isReviewsGalleryTile(element)) return true;
  return isCardElement(element) && shouldFlipCard(element);
}

/**
 * @param {HTMLElement} element
 * @returns {'topeak-reveal--media' | 'topeak-reveal--soft' | 'topeak-reveal--flip' | 'topeak-reveal--fade' | 'topeak-reveal--fade-left' | 'topeak-reveal--step' | 'topeak-reveal--solution' | 'topeak-reveal--product-card' | 'topeak-reveal--rotate-out' | ''}
 */
function getRevealVariant(element) {
  if (element.dataset.topeakReveal === 'fade' || element.matches('.topeak-product-grid__footer, .topeak-btn--product-grid, .topeak-core-features__media, .topeak-about-origin-story-1__item-image-wrap')) {
    return 'topeak-reveal--fade';
  }

  if (isAuthorityTickerPill(element)) {
    return 'topeak-reveal--fade-left';
  }

  if (isRotateOutImage(element)) {
    return 'topeak-reveal--rotate-out';
  }

  if (element.matches('img, video, picture, [class*="__media"], [class*="__image"]')) {
    return 'topeak-reveal--media';
  }

  if (isHowItWorksStep(element)) {
    return 'topeak-reveal--step';
  }

  if (isSolutionCard(element)) {
    return 'topeak-reveal--solution';
  }

  if (isProductCard(element)) {
    return 'topeak-reveal--product-card';
  }

  if (shouldFlip(element)) {
    return 'topeak-reveal--flip';
  }

  if (element.matches('.topeak-btn, [class*="__eyebrow"], [class*="__cta"]')) {
    return 'topeak-reveal--soft';
  }

  return '';
}

/**
 * Alternate flip on odd-indexed cards inside grids for variety.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function shouldFlipCard(element) {
  const index = Number.parseInt(element.dataset.topeakRevealIndex ?? '0', 10);
  return index % 2 === 1;
}

/**
 * @param {HTMLElement} element
 */
function applyFlipAxis(element) {
  const index = Number.parseInt(element.dataset.topeakRevealIndex ?? '0', 10);

  if (isReviewsGalleryTile(element)) {
    element.dataset.topeakRevealAxis = 'y-left';
    return;
  }

  if (index % 4 === 1) {
    element.dataset.topeakRevealAxis = 'y-left';
    return;
  }

  if (index % 4 === 3) {
    element.dataset.topeakRevealAxis = 'y-right';
    return;
  }

  element.dataset.topeakRevealAxis = 'x';
}

/**
 * Drop parent nodes when a descendant is also a target — parent opacity: 0 hides children.
 *
 * @param {HTMLElement[]} targets
 * @returns {HTMLElement[]}
 */
function pruneNestedTargets(targets) {
  return targets.filter((target) => {
    return !targets.some((other) => other !== target && target.contains(other));
  });
}

/**
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
function collectTargets(root) {
  /** @type {Set<HTMLElement>} */
  const targets = new Set();
  const firstSection = document.querySelector('#MainContent .shopify-section:first-of-type');
  const sections = root.querySelectorAll('#MainContent .shopify-section, footer .shopify-section');

  for (const shopifySection of sections) {
    if (!(shopifySection instanceof HTMLElement)) continue;
    if (shopifySection.closest('#header-group, [data-topeak-reveal="off"]')) continue;

    const topeakRoot = shopifySection.querySelector('[class*="topeak-"]');
    if (!(topeakRoot instanceof HTMLElement)) continue;

    const isFirstSection = shopifySection === firstSection;
    const innerSections = topeakRoot.querySelectorAll('.topeak-section');
    const layoutContainers = topeakRoot.querySelectorAll(GRID_SELECTORS);

    if (layoutContainers.length > 0) {
      for (const grid of layoutContainers) {
        if (!(grid instanceof HTMLElement)) continue;

        const children = [...grid.children].filter((child) => isValidTarget(child, isFirstSection));
        for (const [index, child] of children.entries()) {
          if (child.matches(ROTATE_OUT_MEDIA_SELECTOR)) continue;

          targets.add(child);
          if (!isProductCard(child) && index <= 8) {
            child.dataset.topeakRevealIndex = String(index);
          }
        }
      }
    }

    const sectionRoots = innerSections.length > 0 ? innerSections : [topeakRoot];

    for (const media of topeakRoot.querySelectorAll('.topeak-core-features__media')) {
      if (isValidTarget(media, isFirstSection)) {
        targets.add(media);
      }
    }

    for (const list of topeakRoot.querySelectorAll('.topeak-product-grid__list, .topeak-shop-all-product-grid__list')) {
      if (!(list instanceof HTMLElement)) continue;

      const cards = [...list.querySelectorAll(PRODUCT_CARD_SELECTOR)].filter((card) => isValidTarget(card, isFirstSection));

      for (const card of cards) {
        targets.add(card);
      }
    }

    for (const [index, imageWrap] of [...topeakRoot.querySelectorAll('.topeak-about-origin-story-1__item-image-wrap')].entries()) {
      if (!(imageWrap instanceof HTMLElement)) continue;
      if (!isValidTarget(imageWrap, isFirstSection)) continue;

      targets.add(imageWrap);
      if (index <= 8) {
        imageWrap.dataset.topeakRevealIndex = String(index);
      }
    }

    for (const productImage of topeakRoot.querySelectorAll(ROTATE_OUT_IMAGE_SELECTOR)) {
      if (!(productImage instanceof HTMLElement)) continue;
      if (!isValidTarget(productImage, isFirstSection)) continue;

      targets.add(productImage);
    }

    for (const pill of topeakRoot.querySelectorAll('.topeak-authority-ticker__pill')) {
      if (!(pill instanceof HTMLElement)) continue;
      if (!isValidTarget(pill, isFirstSection)) continue;

      targets.add(pill);
    }

    for (const sectionInner of sectionRoots) {
      for (const child of sectionInner.children) {
        if (!isValidTarget(child, isFirstSection)) continue;
        if (isLayoutContainer(child)) continue;
        if (child.matches('.topeak-about-origin-story-1__right, .topeak-authority-ticker__inner')) continue;
        if (child.querySelector(GRID_SELECTORS)) continue;
        targets.add(child);
      }
    }
  }

  return pruneNestedTargets([...targets]);
}

/**
 * @param {HTMLElement} element
 */
function revealElement(element) {
  element.classList.add('is-visible');
  observer?.unobserve(element);
}

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
function getRevealDelay(element) {
  const index = Number.parseInt(element.dataset.topeakRevealIndex ?? '0', 10);
  if (!index) return 0;
  return index * 140;
}

/**
 * @param {HTMLElement} element
 * @param {number} delayMs
 */
function scheduleReveal(element, delayMs) {
  window.setTimeout(() => {
    element.classList.add('is-visible');
  }, delayMs);
}

/**
 * @param {HTMLElement} grid
 */
function revealReviewsGallerySequentially(grid) {
  if (grid.dataset.topeakRevealSequence === 'done' || grid.dataset.topeakRevealSequence === 'active') {
    return;
  }

  const tiles = [...grid.querySelectorAll('.topeak-reviews-1__tile.topeak-reveal')].filter(
    (tile) => tile instanceof HTMLElement && !tile.classList.contains('is-visible')
  );

  if (!tiles.length) return;

  grid.dataset.topeakRevealSequence = 'active';

  for (const [index, tile] of tiles.entries()) {
    scheduleReveal(tile, index * getReviewsRevealStaggerMs());
  }

  const staggerMs = getReviewsRevealStaggerMs();
  const totalMs = (tiles.length - 1) * staggerMs + getReviewsRevealDurationMs();
  window.setTimeout(() => {
    grid.dataset.topeakRevealSequence = 'done';
  }, totalMs);
}

function initReviewsGalleryReveals(root = document) {
  const grids = root.querySelectorAll('.topeak-reviews-1__grid');

  if (!reviewsGridObserver) {
    reviewsGridObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealReviewsGallerySequentially(/** @type {HTMLElement} */ (entry.target));
          reviewsGridObserver?.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '10% 0px -8% 0px',
        threshold: 0.15,
      }
    );
  }

  for (const grid of grids) {
    if (!(grid instanceof HTMLElement)) continue;
    if (grid.dataset.topeakRevealSequence === 'done') continue;

    if (isInViewport(grid)) {
      revealReviewsGallerySequentially(grid);
      continue;
    }

    reviewsGridObserver.observe(grid);
  }
}

/**
 * @param {HTMLElement} grid
 */
function revealHowItWorksSequentially(grid) {
  if (grid.dataset.topeakRevealSequence === 'done' || grid.dataset.topeakRevealSequence === 'active') {
    return;
  }

  const steps = [...grid.querySelectorAll('.topeak-how-it-works__step.topeak-reveal')].filter(
    (step) => step instanceof HTMLElement && !step.classList.contains('is-visible')
  );

  if (!steps.length) return;

  grid.dataset.topeakRevealSequence = 'active';

  for (const [index, step] of steps.entries()) {
    scheduleReveal(step, index * HOW_IT_WORKS_STAGGER_MS);
  }

  const totalMs = (steps.length - 1) * HOW_IT_WORKS_STAGGER_MS + 1000;
  window.setTimeout(() => {
    grid.dataset.topeakRevealSequence = 'done';
  }, totalMs);
}

/**
 * @param {ParentNode} [root]
 */
function initHowItWorksReveals(root = document) {
  const grids = root.querySelectorAll('.topeak-how-it-works__grid');

  if (!howItWorksGridObserver) {
    howItWorksGridObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealHowItWorksSequentially(/** @type {HTMLElement} */ (entry.target));
          howItWorksGridObserver?.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '10% 0px -8% 0px',
        threshold: 0.15,
      }
    );
  }

  for (const grid of grids) {
    if (!(grid instanceof HTMLElement)) continue;
    if (grid.dataset.topeakRevealSequence === 'done') continue;

    if (isInViewport(grid)) {
      revealHowItWorksSequentially(grid);
      continue;
    }

    howItWorksGridObserver.observe(grid);
  }
}

/**
 * @param {HTMLElement} cards
 */
function revealSolutionCardsTogether(cards) {
  if (cards.dataset.topeakRevealSequence === 'done' || cards.dataset.topeakRevealSequence === 'active') {
    return;
  }

  const solutionCards = [...cards.querySelectorAll('.topeak-solution__card.topeak-reveal')].filter(
    (card) => card instanceof HTMLElement && !card.classList.contains('is-visible')
  );

  if (!solutionCards.length) return;

  cards.dataset.topeakRevealSequence = 'active';

  for (const card of solutionCards) {
    scheduleReveal(card, 0);
  }

  window.setTimeout(() => {
    cards.dataset.topeakRevealSequence = 'done';
  }, 1000);
}

/**
 * @param {ParentNode} [root]
 */
function initSolutionCardsReveals(root = document) {
  const cardGroups = root.querySelectorAll('.topeak-solution__cards');

  if (!solutionCardsObserver) {
    solutionCardsObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealSolutionCardsTogether(/** @type {HTMLElement} */ (entry.target));
          solutionCardsObserver?.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '10% 0px -8% 0px',
        threshold: 0.15,
      }
    );
  }

  for (const cards of cardGroups) {
    if (!(cards instanceof HTMLElement)) continue;
    if (cards.dataset.topeakRevealSequence === 'done') continue;

    if (isInViewport(cards)) {
      revealSolutionCardsTogether(cards);
      continue;
    }

    solutionCardsObserver.observe(cards);
  }
}

/**
 * @param {HTMLElement[]} elements
 * @returns {HTMLElement[]}
 */
function sortElementsByVisualOrder(elements) {
  if (elements.length < 2) {
    return elements;
  }

  const positioned = elements.map((element) => ({
    element,
    rect: element.getBoundingClientRect(),
  }));

  const hasLayout = positioned.some(({ rect }) => rect.width > 0 || rect.height > 0);
  if (!hasLayout) {
    return elements;
  }

  const rowThreshold = 8;

  return positioned
    .sort((a, b) => {
      const rowDelta = a.rect.top - b.rect.top;
      if (Math.abs(rowDelta) > rowThreshold) {
        return rowDelta;
      }

      return a.rect.left - b.rect.left;
    })
    .map(({ element }) => element);
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getProductCardsInContainer(container) {
  const cards = [...container.querySelectorAll(`${PRODUCT_CARD_SELECTOR}.topeak-reveal`)].filter(
    (card) => card instanceof HTMLElement && !card.classList.contains('is-visible')
  );

  return sortElementsByVisualOrder(cards);
}

/**
 * @returns {number}
 */
function getProductCardFlipDurationMs() {
  return window.matchMedia(PRODUCT_CARD_FLIP_DESKTOP_MQ).matches
    ? PRODUCT_CARD_FLIP_DURATION_MS
    : STEP_REVEAL_DURATION_MS;
}

/**
 * @returns {number}
 */
function getProductCardFlipStaggerMs() {
  return window.matchMedia(PRODUCT_CARD_FLIP_DESKTOP_MQ).matches
    ? PRODUCT_CARD_FLIP_STAGGER_DESKTOP_MS
    : HOW_IT_WORKS_STAGGER_MS;
}

/**
 * @param {HTMLElement} container
 */
function revealProductCardsSequentially(container) {
  if (container.hidden) return;

  const sequenceKey = container.dataset.topeakRevealSequence;
  if (sequenceKey === 'done' || sequenceKey === 'active') {
    return;
  }

  const cards = getProductCardsInContainer(container);
  if (!cards.length) return;

  const staggerMs = getProductCardFlipStaggerMs();
  const durationMs = getProductCardFlipDurationMs();

  container.dataset.topeakRevealSequence = 'active';

  for (const [index, card] of cards.entries()) {
    scheduleReveal(card, index * staggerMs);
  }

  const totalMs = (cards.length - 1) * staggerMs + durationMs;
  window.setTimeout(() => {
    container.dataset.topeakRevealSequence = 'done';
  }, totalMs);
}

/**
 * @param {HTMLElement} container
 */
function setupProductCardsContainer(container) {
  if (!(container instanceof HTMLElement)) return;
  if (container.hidden) return;
  if (container.dataset.topeakRevealSequence === 'done') return;

  if (isInViewport(container)) {
    revealProductCardsSequentially(container);
    return;
  }

  productCardsListObserver?.observe(container);
}

/**
 * @param {ParentNode} [root]
 */
function initProductCardsReveals(root = document) {
  const sections = root.querySelectorAll(PRODUCT_GRID_SECTION_SELECTOR);

  if (!productCardsListObserver) {
    productCardsListObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealProductCardsSequentially(/** @type {HTMLElement} */ (entry.target));
          productCardsListObserver?.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '10% 0px -8% 0px',
        threshold: 0.1,
      }
    );
  }

  for (const section of sections) {
    if (!(section instanceof HTMLElement)) continue;

    if (section.classList.contains('topeak-shop-all-product-grid')) {
      for (const panel of section.querySelectorAll('.topeak-shop-all-product-grid__panel')) {
        setupProductCardsContainer(/** @type {HTMLElement} */ (panel));
      }
      continue;
    }

    setupProductCardsContainer(getHomeProductGridContainer(section));
  }
}

/**
 * @param {HTMLElement} section
 * @returns {HTMLElement}
 */
function getHomeProductGridContainer(section) {
  const list = section.querySelector('.topeak-product-grid__list');
  return list instanceof HTMLElement ? list : section;
}

/**
 * @param {HTMLElement} list
 */
function revealAuthorityTickerPillsSequentially(list) {
  if (list.dataset.topeakRevealSequence === 'done' || list.dataset.topeakRevealSequence === 'active') {
    return;
  }

  const pills = [...list.querySelectorAll('.topeak-authority-ticker__pill.topeak-reveal')].filter(
    (pill) => pill instanceof HTMLElement && !pill.classList.contains('is-visible')
  );

  if (!pills.length) return;

  list.dataset.topeakRevealSequence = 'active';

  for (const [index, pill] of pills.entries()) {
    scheduleReveal(pill, index * AUTHORITY_TICKER_PILL_STAGGER_MS);
  }

  const totalMs = (pills.length - 1) * AUTHORITY_TICKER_PILL_STAGGER_MS + 950;
  window.setTimeout(() => {
    list.dataset.topeakRevealSequence = 'done';
  }, totalMs);
}

/**
 * @param {ParentNode} [root]
 */
function initAuthorityTickerReveals(root = document) {
  const lists = root.querySelectorAll('.topeak-authority-ticker__pills');

  if (!authorityTickerObserver) {
    authorityTickerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealAuthorityTickerPillsSequentially(/** @type {HTMLElement} */ (entry.target));
          authorityTickerObserver?.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '10% 0px -8% 0px',
        threshold: 0.15,
      }
    );
  }

  for (const list of lists) {
    if (!(list instanceof HTMLElement)) continue;
    if (list.dataset.topeakRevealSequence === 'done') continue;

    if (isInViewport(list)) {
      revealAuthorityTickerPillsSequentially(list);
      continue;
    }

    authorityTickerObserver.observe(list);
  }
}

/**
 * @param {HTMLElement} element
 * @param {boolean} isFirstSection
 */
function activateReveal(element, isFirstSection) {
  const variant = getRevealVariant(element);
  element.classList.add('topeak-reveal', ...(variant ? [variant] : []));

  if (variant === 'topeak-reveal--flip') {
    applyFlipAxis(element);
  }

  if (isProductCard(element)) {
    delete element.dataset.topeakRevealIndex;
  }

  if (isReviewsGalleryTile(element) || isHowItWorksStep(element) || isSolutionCard(element) || isProductCard(element) || isAuthorityTickerPill(element)) {
    return;
  }

  if (isFirstSection) {
    scheduleReveal(element, FIRST_SECTION_BASE_DELAY_MS + getRevealDelay(element));
    return;
  }

  if (isInViewport(element)) {
    scheduleReveal(element, IN_VIEW_BASE_DELAY_MS + getRevealDelay(element));
    return;
  }

  observer?.observe(element);
}

/**
 * @param {ParentNode} [root]
 */
function initReveal(root = document) {
  if (prefersReducedMotion()) return;

  const firstSection = document.querySelector('#MainContent .shopify-section:first-of-type');
  const targets = collectTargets(root);

  if (targets.length) {
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            revealElement(/** @type {HTMLElement} */ (entry.target));
          }
        },
        {
          root: null,
          rootMargin: '12% 0px -8% 0px',
          threshold: 0.05,
        }
      );
    }

    for (const element of targets) {
      if (element.classList.contains('topeak-reveal')) continue;

      const isFirstSection = firstSection?.contains(element) && element.closest('.shopify-section') === firstSection;
      activateReveal(element, Boolean(isFirstSection));
    }
  }

  initReviewsGalleryReveals(root);
  initHowItWorksReveals(root);
  initSolutionCardsReveals(root);
  initProductCardsReveals(root);
  initAuthorityTickerReveals(root);
}

onDocumentReady(() => {
  initReveal();
});

document.addEventListener(PRODUCT_CARDS_REVEAL_EVENT, (event) => {
  const container = event.target;
  if (!(container instanceof HTMLElement)) return;

  if (container.dataset.topeakRevealSequence === 'done') {
    container.dataset.topeakRevealSequence = '';
  }

  revealProductCardsSequentially(container);
});

document.addEventListener('topeak:reviews-2-loaded', (event) => {
  const section = event.target;
  if (!(section instanceof HTMLElement)) return;

  initReveal(section);
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initReveal(event.target);
  }
});

window.addEventListener('pagereveal', () => {
  initReveal();
});
